import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { ArrowLeft, Mail, Phone, Video, FileText, Edit, Trash2, Copy, ExternalLink, ClipboardList, Lock, Loader2, Sparkles, Brain, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../../lib/dateUtils';

const statusConfigs = {
  new: { label: 'New', color: 'hsl(221 83% 53%)', bgColor: 'hsl(221 83% 96%)', textColor: 'hsl(221 83% 53%)' },
  contacted: { label: 'Contacted', color: 'hsl(45 93% 47%)', bgColor: 'hsl(45 93% 95%)', textColor: 'hsl(45 93% 35%)' },
  qualified: { label: 'Qualified', color: 'hsl(262 83% 58%)', bgColor: 'hsl(262 83% 97%)', textColor: 'hsl(262 83% 48%)' },
  converted: { label: 'Converted', color: 'hsl(142 76% 36%)', bgColor: 'hsl(142 76% 96%)', textColor: 'hsl(142 76% 30%)' },
  lost: { label: 'Lost', color: 'hsl(0 84% 60%)', bgColor: 'hsl(0 84% 97%)', textColor: 'hsl(0 84% 45%)' }
};

export function LeadProfileSidebar({
  lead, currency, brainCalls,
  onBack, onEdit, onDelete,
  onQuickNote, onQuickEmail, onQuickCall, onQuickTask, onQuickMeeting,
  onAICallClick,
  onRevealContact, revealing,
  getUserName,
}) {
  if (!lead) return null;

  // Who created this lead (teammate name, or a friendly label for system creators).
  const creatorLabel = (() => {
    const cb = lead.created_by;
    if (!cb) return null;
    if (cb === 'ai-brain' || lead.ai_generated) return 'AI Brain';
    if (lead.import_id) return typeof getUserName === 'function' ? `${getUserName(cb)} (import)` : 'Imported';
    return typeof getUserName === 'function' ? getUserName(cb) : null;
  })();

  return (
    <div className="w-96 shrink-0 flex flex-col bg-background" data-testid="lead-left-column">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 h-12 border-b border-border shrink-0">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-button">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
          {lead.name?.charAt(0)?.toUpperCase()}
        </div>
        <h2 className="text-base font-bold truncate flex-1">{lead.name}</h2>
        <button onClick={onEdit} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="edit-lead-header-btn" title="Edit lead">
          <Edit className="h-4 w-4" />
        </button>
        {onDelete && (
          <button onClick={onDelete} className="text-muted-foreground hover:text-rose-600 transition-colors" data-testid="delete-lead-header-btn" title="Delete lead">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {/* Quick Actions */}
        <div className="flex items-center justify-between gap-2 py-4 border-b border-border mb-5">
          <QuickAction icon={FileText} label="Note" onClick={onQuickNote} testId="action-note" />
          <QuickAction icon={Mail} label="Email" onClick={onQuickEmail} testId="action-email" />
          <QuickAction icon={Phone} label="Call" onClick={onQuickCall} testId="action-call" />
          <QuickAction icon={ClipboardList} label="Task" onClick={onQuickTask} testId="action-task" />
          <QuickAction icon={Video} label="Meet" onClick={onQuickMeeting} testId="action-meet" />
          <button className="flex flex-col items-center gap-1.5 group relative" onClick={onAICallClick} data-testid="action-ai-call">
            <div className="h-10 w-10 rounded-lg border border-violet-500/30 bg-violet-500/10 flex items-center justify-center group-hover:bg-violet-500/20 group-hover:border-violet-500/50 transition-colors relative">
              <Brain className="h-4 w-4 text-violet-500" />
              {brainCalls.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-violet-600 text-white text-[9px] font-bold px-1 shadow-sm">
                  {brainCalls.length}
                </span>
              )}
            </div>
            <span className="text-[10px] text-foreground">AI Call</span>
          </button>
        </div>

        {/* Key Information */}
        <div className="space-y-4">
          <div className="border border-border rounded-lg mt-4">
            <Accordion type="single" collapsible defaultValue="key-info" className="w-full">
              <AccordionItem value="key-info" className="border-none">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50 rounded-t-lg">
                  <span className="text-sm font-semibold">Key Information</span>
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  <div className="divide-y divide-border">
                    <InfoRow label="Email">
                      {lead.email ? (
                        <CopyableValue value={lead.email} testId="lead-detail-email" />
                      ) : (
                        <RevealableField lead={lead} field="email" onReveal={onRevealContact} revealing={revealing} />
                      )}
                    </InfoRow>
                    <InfoRow label="Phone">
                      {lead.phone ? (
                        <CopyableValue value={lead.phone} testId="lead-detail-phone" />
                      ) : (
                        <RevealableField lead={lead} field="phone" onReveal={onRevealContact} revealing={revealing} showReveal={!lead.email} />
                      )}
                    </InfoRow>
                    {lead.personal_emails?.length > 0 && (
                      <InfoRow label="Personal Emails">
                        {lead.personal_emails.map((em, i) => (
                          <CopyableValue key={i} value={em} />
                        ))}
                      </InfoRow>
                    )}
                    {lead.linkedin_url && (
                      <InfoRow label="LinkedIn">
                        <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline flex items-center gap-1" data-testid="lead-linkedin-link">
                          <Linkedin className="h-3 w-3" /> Profile <ExternalLink className="h-3 w-3" />
                        </a>
                      </InfoRow>
                    )}
                    {(lead.title || lead.designation) && (
                      <InfoRow label="Title"><p className="text-sm font-medium">{lead.title || lead.designation}</p></InfoRow>
                    )}
                    <InfoRow label="Company"><p className="text-sm font-medium">{lead.company || '-'}</p></InfoRow>
                    <InfoRow label="Source">
                      {lead.source === 'lockene_search' ? (
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" data-testid="discovery-source-badge">
                            <Sparkles className="h-3 w-3" />Discover Leads
                          </span>
                          {lead.discovery_source?.domain && <span className="text-[10px] text-muted-foreground">{lead.discovery_source.domain}</span>}
                        </div>
                      ) : <p className="text-sm font-medium">{lead.source || '-'}</p>}
                    </InfoRow>
                    <InfoRow label="Expected Value"><p className="text-sm font-medium">{currency.format(parseFloat(lead.value || 0))}</p></InfoRow>
                    {(lead.interested_product_name || lead.interested_product_id) && (
                      <InfoRow label="Product / Service" testId="lead-product-service">
                        <p className="text-sm font-medium">{lead.interested_product_name || '—'}</p>
                      </InfoRow>
                    )}
                    {lead.assigned_to && (
                      <InfoRow label="Assigned To" testId="lead-assigned-to">
                        <p className="text-sm font-medium">{typeof getUserName === 'function' ? getUserName(lead.assigned_to) : '—'}</p>
                      </InfoRow>
                    )}
                    <InfoRow label="Status">
                      <Badge variant="outline" className="text-xs" style={{ backgroundColor: statusConfigs[lead.status]?.bgColor, color: statusConfigs[lead.status]?.textColor }}>
                        {statusConfigs[lead.status]?.label || lead.status}
                      </Badge>
                    </InfoRow>
                    <InfoRow label="Created"><p className="text-sm font-medium">{lead.created_at ? formatLocalDate(lead.created_at) : '-'}</p></InfoRow>
                    {creatorLabel && (
                      <InfoRow label="Created by" testId="lead-created-by">
                        <p className="text-sm font-medium">{creatorLabel}</p>
                      </InfoRow>
                    )}
                    <InfoRow label="Last Updated"><p className="text-sm font-medium">{lead.updated_at ? formatLocalDate(lead.updated_at) : '-'}</p></InfoRow>
                    {(lead.address || lead.city || lead.state || lead.country) && (
                      <InfoRow label="Address">
                        <p className="text-sm font-medium">{[lead.address, lead.city, lead.state, lead.postal_code, lead.country].filter(Boolean).join(', ')}</p>
                      </InfoRow>
                    )}
                    {(lead.detected_market || lead.detected_accent) && (
                      <InfoRow label="Voice Market" testId="lead-market-info">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{lead.detected_market || '—'}</span>
                          {lead.detected_accent && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary">
                              {lead.detected_accent?.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </InfoRow>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button className="flex flex-col items-center gap-1.5 group" onClick={onClick} data-testid={testId}>
      <div className="h-10 w-10 rounded-lg border border-border flex items-center justify-center group-hover:bg-muted group-hover:border-primary/30 transition-colors">
        <Icon className="h-4 w-4 text-foreground" />
      </div>
      <span className="text-[10px] text-foreground">{label}</span>
    </button>
  );
}

function InfoRow({ label, children, testId }) {
  return (
    <div className="py-2.5 px-4" data-testid={testId}>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      {children}
    </div>
  );
}

function CopyableValue({ value, testId }) {
  return (
    <div className="flex items-center gap-2">
      <p className="text-sm font-medium truncate flex-1" data-testid={testId}>{value}</p>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied'); }} className="text-muted-foreground hover:text-foreground shrink-0">
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}

function RevealableField({ lead, field, onReveal, revealing, showReveal = true }) {
  const isApolloLead = lead.apollo_id || lead.provider_id || (lead.source || '').toLowerCase().includes('apollo');
  if (!isApolloLead) return <span className="text-sm text-muted-foreground">-</span>;
  return (
    <div className="flex items-center gap-2">
      <Lock className="h-3 w-3 text-amber-500" />
      <span className="text-sm text-muted-foreground">Not available</span>
      {!lead.contact_revealed && showReveal && (
        <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px] text-amber-600 hover:text-amber-700 dark:text-amber-400" onClick={onReveal} disabled={revealing} data-testid={`reveal-${field}-btn`}>
          {revealing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Reveal'}
        </Button>
      )}
    </div>
  );
}
